/**
 * Products API Transformers
 * Transform between API DTOs and Domain types
 */

import type {
  SKU,
  SKUSummary,
  Variant,
  VariantSummary,
  Category,
  CategorySummary,
  LocalizedString,
  MediaAsset,
  CreateSKURequest,
  UpdateSKURequest,
  CreateVariantRequest,
  UpdateVariantRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '../domain/types'
import type {
  SKUDto,
  SKUSummaryDto,
  VariantDto,
  VariantSummaryDto,
  CategoryDto,
  CategorySummaryDto,
} from './api-types'
import { mergeLocalizationsStrict } from '@ufc/core'
import type { StrapiLocalizedDto } from '@ufc/core'

// ============================================================================
// Localized String Helpers (Strapi i18n + localizations merge)
// ============================================================================

function toLocalizedString(dto: StrapiLocalizedDto, field: string): LocalizedString {
  return mergeLocalizationsStrict(dto, field) as LocalizedString
}

function optionalDescription(dto: StrapiLocalizedDto): LocalizedString | undefined {
  const merged = mergeLocalizationsStrict(dto, 'description')
  const en = merged.en?.trim()
  const ar = merged.ar?.trim()
  if (!en && !ar) return undefined
  return merged as LocalizedString
}

function asStrapiDto<T extends object>(dto: T): StrapiLocalizedDto {
  return dto as unknown as StrapiLocalizedDto
}

function toApiLocalizedString(value: LocalizedString): string {
  // For API requests, we send the English value
  // Arabic translations are handled via Strapi's i18n plugin
  return value.en
}

function extractMediaUrl(media: unknown): string | undefined {
  if (!media) return undefined

  if (typeof media === 'string') {
    return media
  }

  if (typeof media === 'object') {
    const candidate = media as { url?: unknown; data?: unknown }

    if (typeof candidate.url === 'string') {
      return candidate.url
    }

    // Handle nested Strapi media structures when present.
    if (candidate.data && typeof candidate.data === 'object') {
      const nested = candidate.data as { url?: unknown; attributes?: unknown }
      if (typeof nested.url === 'string') {
        return nested.url
      }

      if (nested.attributes && typeof nested.attributes === 'object') {
        const attrs = nested.attributes as { url?: unknown }
        if (typeof attrs.url === 'string') {
          return attrs.url
        }
      }
    }
  }

  return undefined
}

function extractMediaId(media: unknown): string | undefined {
  if (!media || typeof media !== 'object') return undefined

  const candidate = media as { id?: unknown; data?: unknown }
  if (typeof candidate.id === 'string' || typeof candidate.id === 'number') {
    return String(candidate.id)
  }

  if (candidate.data && typeof candidate.data === 'object') {
    const nested = candidate.data as { id?: unknown }
    if (typeof nested.id === 'string' || typeof nested.id === 'number') {
      return String(nested.id)
    }
  }

  return undefined
}

function toMediaAsset(media: unknown): MediaAsset | undefined {
  const id = extractMediaId(media)
  const url = extractMediaUrl(media)
  if (!id || !url) return undefined
  return { id, url }
}

function toStrapiMediaIds(ids: string[] | undefined): Array<string | number> | undefined {
  if (!ids) return undefined
  return ids.map((id) => {
    const asNumber = Number(id)
    return Number.isFinite(asNumber) ? asNumber : id
  })
}

// ============================================================================
// Category Transformers
// ============================================================================

export function transformCategorySummaryDto(dto: CategorySummaryDto): CategorySummary {
  const d = asStrapiDto(dto)
  return {
    id: dto.id,
    documentId: dto.documentId,
    code: dto.code,
    name: toLocalizedString(d, 'name'),
    color: dto.color ?? null,
    isActive: dto.isActive,
  }
}

export function transformCategoryDto(dto: CategoryDto): Category {
  const d = asStrapiDto(dto)
  return {
    id: dto.id,
    documentId: dto.documentId,
    code: dto.code,
    name: toLocalizedString(d, 'name'),
    description: optionalDescription(d),
    color: dto.color ?? null,
    isActive: dto.isActive,
    parent: dto.parent ? transformCategorySummaryDto(dto.parent) : null,
    skuCount: dto.skuCount,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

// ============================================================================
// SKU Transformers
// ============================================================================

export function transformSKUSummaryDto(dto: SKUSummaryDto): SKUSummary {
  const d = asStrapiDto(dto)
  return {
    id: dto.id,
    documentId: dto.documentId,
    name: toLocalizedString(d, 'name'),
    isActive: dto.isActive,
    variantCount: dto.variantCount,
  }
}

export function transformSKUDto(dto: SKUDto): SKU {
  const d = asStrapiDto(dto)
  // Handle variants - can be array or count object from Strapi
  const variantsArray = Array.isArray(dto.variants) ? dto.variants : []
  const variantCount = dto.variantCount
    ?? (dto.variants as { count?: number } | undefined)?.count
    ?? variantsArray.length
    ?? 0

  return {
    id: dto.id,
    documentId: dto.documentId,
    name: toLocalizedString(d, 'name'),
    description: optionalDescription(d),
    isActive: dto.isActive,
    categories: Array.isArray(dto.categories) ? dto.categories.map(transformCategorySummaryDto) : [],
    variants: variantsArray.map(transformVariantSummaryDto),
    variantCount,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

export function transformCreateSKURequest(request: CreateSKURequest): Record<string, unknown> {
  return {
    name: toApiLocalizedString(request.name),
    description: request.description ? toApiLocalizedString(request.description) : null,
    isActive: request.isActive,
    categories: request.categoryDocumentIds ?? [],
  }
}

export function transformUpdateSKURequest(request: UpdateSKURequest): Record<string, unknown> {
  return {
    name: toApiLocalizedString(request.name),
    description: request.description ? toApiLocalizedString(request.description) : null,
    isActive: request.isActive,
    categories: request.categoryDocumentIds ?? [],
  }
}

// ============================================================================
// Variant Transformers
// ============================================================================

export function transformVariantSummaryDto(dto: VariantSummaryDto): VariantSummary {
  const v = asStrapiDto(dto)
  return {
    id: dto.id,
    documentId: dto.documentId,
    name: toLocalizedString(v, 'name'),
    variantCode: dto.variantCode,
    barcode: dto.barcode,
    weight: dto.weight,
    packaging: dto.packaging,
    portalAvailability: dto.portalAvailability ?? 'both',
    isActive: dto.isActive,
    sapMaterialCode: dto.sapMaterialCode ?? undefined,
    sapStorageLocationCode: dto.sapStorageLocationCode ?? undefined,
  }
}

export function transformVariantDto(dto: VariantDto): Variant {
  const v = asStrapiDto(dto)
  const imagesArray = Array.isArray(dto.images) ? dto.images : []
  const imageMedia = imagesArray
    .map(toMediaAsset)
    .filter((image): image is MediaAsset => Boolean(image))

  return {
    id: dto.id,
    documentId: dto.documentId,
    name: toLocalizedString(v, 'name'),
    description: optionalDescription(v),
    variantCode: dto.variantCode,
    barcode: dto.barcode,
    weight: dto.weight,
    packaging: dto.packaging,
    portalAvailability: dto.portalAvailability ?? 'both',
    deliveryGroupingRule: dto.deliveryGroupingRule,
    sapMaterialCode: dto.sapMaterialCode ?? undefined,
    sapStorageLocationCode: dto.sapStorageLocationCode ?? undefined,
    sapDescription: dto.sapDescription ?? undefined,
    sapBaseUnit: dto.sapBaseUnit ?? undefined,
    sapLastChanged: dto.sapLastChanged ?? undefined,
    images: imagesArray.map(extractMediaUrl).filter((url): url is string => Boolean(url)),
    imageMedia,
    isActive: dto.isActive,
    sku: dto.sku ? transformSKUSummaryDto(dto.sku) : undefined,
    skuDocumentId: dto.sku?.documentId,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    priceItemDocumentId: null,
    minQuantity: dto.minQuantity ?? null,
    maxQuantity: dto.maxQuantity ?? null,
    minOrderQuantity: dto.minOrderQuantity ?? null,
    maxOrderQuantity: dto.maxOrderQuantity ?? null,
  }
}

export function transformCreateVariantRequest(
  request: CreateVariantRequest
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    sku: request.skuDocumentId,
    name: toApiLocalizedString(request.name),
    description: request.description ? toApiLocalizedString(request.description) : null,
    variantCode: request.variantCode,
    barcode: request.barcode,
    weight: request.weight,
    packaging: request.packaging,
    portalAvailability: request.portalAvailability,
    deliveryGroupingRule: request.deliveryGroupingRule,
    isActive: request.isActive,
    sapMaterialCode: request.sapMaterialCode || null,
    sapStorageLocationCode: request.sapStorageLocationCode || null,
    sapDescription: request.sapDescription || null,
    sapBaseUnit: request.sapBaseUnit || null,
    minQuantity: request.minQuantity ?? null,
    maxQuantity: request.maxQuantity ?? null,
    minOrderQuantity: request.minOrderQuantity ?? null,
    maxOrderQuantity: request.maxOrderQuantity ?? null,
  }
  const mediaIds = toStrapiMediaIds(request.imageIds)
  if (mediaIds !== undefined) {
    payload.images = mediaIds
  }
  return payload
}

export function transformUpdateVariantRequest(
  request: UpdateVariantRequest
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    sku: request.skuDocumentId,
    name: toApiLocalizedString(request.name),
    description: request.description ? toApiLocalizedString(request.description) : null,
    variantCode: request.variantCode,
    barcode: request.barcode,
    weight: request.weight,
    packaging: request.packaging,
    portalAvailability: request.portalAvailability,
    deliveryGroupingRule: request.deliveryGroupingRule,
    isActive: request.isActive,
    sapMaterialCode: request.sapMaterialCode || null,
    sapStorageLocationCode: request.sapStorageLocationCode || null,
    sapDescription: request.sapDescription || null,
    sapBaseUnit: request.sapBaseUnit || null,
    minQuantity: request.minQuantity ?? null,
    maxQuantity: request.maxQuantity ?? null,
    minOrderQuantity: request.minOrderQuantity ?? null,
    maxOrderQuantity: request.maxOrderQuantity ?? null,
  }
  const mediaIds = toStrapiMediaIds(request.imageIds)
  if (mediaIds !== undefined) {
    payload.images = mediaIds
  }
  return payload
}

// ============================================================================
// Category Request Transformers
// ============================================================================

export function transformCreateCategoryRequest(
  request: CreateCategoryRequest
): Record<string, unknown> {
  return {
    name: toApiLocalizedString(request.name),
    description: request.description ? toApiLocalizedString(request.description) : null,
    code: request.code || null,
    color: request.color || null,
    parent: request.parentDocumentId || null,
    isActive: request.isActive,
  }
}

export function transformUpdateCategoryRequest(
  request: UpdateCategoryRequest
): Record<string, unknown> {
  return {
    name: toApiLocalizedString(request.name),
    description: request.description ? toApiLocalizedString(request.description) : null,
    code: request.code || null,
    color: request.color || null,
    parent: request.parentDocumentId || null,
    isActive: request.isActive,
  }
}
